/**
 * UNR155Tree — the 3-level tree-table body (sections → categories → items).
 *
 * Step 4 builds the data adapter + the DataTable wiring with default
 * styling. Step 5 adds per-depth row backgrounds. Step 6 makes the
 * Relevance + Actions columns interactive.
 *
 * Backend data contract (frozen — no new fields read):
 *   GET /api/v1/session/{session_id}/unr155-checklist returns
 *   { threats: [{ category_id, category_number, category_title,
 *                 section, items: [{ item_id, description, relevance,
 *                                    rationale, updated_at }] }],
 *     completion_stats: { total, relevant, not_relevant, unset } }
 *
 *   The "section" level (depth 0) is a client-side grouping — we
 *   derive one synthetic row per distinct `section` value found in
 *   threats[]. No new fields are invented; the synthetic row only
 *   carries display text (label, counts) computed from the existing
 *   threats[].
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DataTable from '../../../components/ui/DataTable/DataTable';
import RowActionsMenu from '../../../components/ui/DataTable/RowActionsMenu';
import TableToolbar from '../../../components/ui/DataTable/TableToolbar';
import FilterDrawer from '../../../components/ui/DataTable/FilterDrawer';
import dtStyles from '../../../components/ui/DataTable/DataTable.module.css';
import CategoryPill from '../../../components/ui/CategoryPill';
import { getUNR155Checklist, updateUNR155Item } from '../api';
import RationaleEditor from './RationaleEditor';
import RelevanceCell from './RelevanceCell';
import RationaleCell from './RationaleCell';
import styles from './UNR155Tree.module.css';

// Section labels — ported verbatim from the legacy
// `features/athena/Results/UNR155ChecklistSection.jsx`.
const SECTION_LABELS = {
  back_end_servers: 'Threats regarding back-end servers related to vehicles',
  communication_channels: 'Threats to vehicles regarding their communication channel',
  update_procedures: 'Threats to vehicles regarding their update procedures',
  unintended_human_actions: 'Threats to vehicles regarding unintended human actions',
  external_connectivity: 'Threats to vehicles regarding their external connectivity and connections',
  attack_motivations: 'Potential targets of, or motivations for, an attack',
  vulnerabilities: 'Potential vulnerabilities that could be exploited if not sufficiently protected or hardened',
};

// Update an item in the flat threats[] tree. Returns a new threats[]
// with `partial` shallow-merged into the matching item. Used for the
// optimistic update on relevance/rationale change.
function applyItemUpdate(threats, itemId, partial) {
  return threats.map((threat) => ({
    ...threat,
    items: (threat.items || []).map((item) =>
      item.item_id === itemId ? { ...item, ...partial } : item,
    ),
  }));
}

/**
 * Pure adapter: backend's flat `threats[]` (each tagged with a
 * `section` key) → 3-level tree compatible with DataTable's
 * `getRowChildren` contract. Section ordering follows the order
 * threats are first seen in (matches the canonical service-side
 * ordering since the backend groups categories by section).
 */
function toTreeRows(threats) {
  const bySection = new Map();
  for (const threat of threats || []) {
    const sectionKey = threat.section;
    if (!bySection.has(sectionKey)) bySection.set(sectionKey, []);
    bySection.get(sectionKey).push(threat);
  }

  return Array.from(bySection.entries()).map(([sectionKey, categories]) => {
    const catRows = categories.map((cat) => ({
      id: `category:${cat.category_id}`,
      _kind: 'category',
      category_id: cat.category_id,
      category_number: cat.category_number,
      category_title: cat.category_title,
      children: (cat.items || []).map((item) => ({
        id: `item:${item.item_id}`,
        _kind: 'item',
        item_id: item.item_id,
        description: item.description,
        relevance: item.relevance,
        rationale: item.rationale,
        updated_at: item.updated_at,
      })),
    }));
    const itemCount = catRows.reduce((sum, c) => sum + c.children.length, 0);
    return {
      id: `section:${sectionKey}`,
      _kind: 'section',
      sectionKey,
      label: SECTION_LABELS[sectionKey] ?? sectionKey,
      categoryCount: catRows.length,
      itemCount,
      children: catRows,
    };
  });
}

const UNR155Tree = ({ sessionId }) => {
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  // Lifted edit state so the kebab "Edit rationale" action and the
  // inline rationale-cell click open the same editor.
  const [editingItemId, setEditingItemId] = useState(null);
  const [savingItemId, setSavingItemId] = useState(null);
  // Canonical filter state — multi-select against the three
  // relevance values. Empty array = no filter applied.
  const [filterOpen, setFilterOpen] = useState(false);
  const [relevanceFilters, setRelevanceFilters] = useState([]);

  const handleUpdateItem = useCallback(
    async (itemId, partial) => {
      // Find current values so we always send the FULL payload the
      // backend expects (item_id + relevance + rationale).
      const currentItem = threats
        .flatMap((t) => t.items || [])
        .find((it) => it.item_id === itemId);
      if (!currentItem) return;
      const next = { ...currentItem, ...partial };

      // Optimistic update.
      setThreats((prev) => applyItemUpdate(prev, itemId, partial));
      setSavingItemId(itemId);
      try {
        await updateUNR155Item(sessionId, {
          item_id: itemId,
          relevance: next.relevance,
          rationale: next.rationale,
        });
      } catch (err) {
        console.error('Failed to update UN R155 item:', err);
        // No automatic revert for now — the next refresh will reconcile.
      } finally {
        setSavingItemId(null);
      }
    },
    [sessionId, threats],
  );

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const fetchChecklist = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUNR155Checklist(sessionId);
        if (cancelled) return;
        setThreats(data?.threats ?? []);
        // Default: every section expanded so the user lands on a
        // populated tree (matches the legacy's open-by-default UX).
        const sectionIds = Array.from(
          new Set((data?.threats ?? []).map((t) => `section:${t.section}`)),
        );
        setExpandedIds(sectionIds);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load UN R155 checklist');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchChecklist();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const treeRows = useMemo(() => toTreeRows(threats), [threats]);

  // Stats derived client-side from threats[].items[*].relevance so
  // they update instantly on each optimistic relevance change. The
  // backend also returns a `completion_stats` object on the response;
  // we re-derive from items to avoid keeping it in sync separately —
  // both sources compute the same four numbers from the same fields.
  const stats = useMemo(() => {
    const items = threats.flatMap((t) => t.items || []);
    const total = items.length;
    const relevant = items.filter((i) => i.relevance === 'relevant').length;
    const notRelevant = items.filter((i) => i.relevance === 'not_relevant').length;
    const unset = total - relevant - notRelevant;
    return { total, relevant, notRelevant, unset };
  }, [threats]);

  // Filtered tree — applies the FilterDrawer's relevance multi-select
  // against item rows, then prunes empty categories + empty sections.
  // Empty filter array = pass-through.
  const filteredTreeRows = useMemo(() => {
    if (relevanceFilters.length === 0) return treeRows;
    return treeRows
      .map((section) => ({
        ...section,
        children: section.children
          .map((category) => ({
            ...category,
            children: category.children.filter((item) =>
              relevanceFilters.includes(item.relevance),
            ),
          }))
          .filter((category) => category.children.length > 0),
      }))
      .filter((section) => section.children.length > 0);
  }, [treeRows, relevanceFilters]);

  const filteredItemCount = useMemo(
    () =>
      filteredTreeRows.reduce(
        (sum, sec) =>
          sum + sec.children.reduce((s, cat) => s + cat.children.length, 0),
        0,
      ),
    [filteredTreeRows],
  );

  const filters = useMemo(
    () => [
      {
        key: 'relevance',
        label: 'Relevance',
        values: relevanceFilters,
        onChange: setRelevanceFilters,
        options: [
          { value: 'relevant', label: 'Relevant', count: stats.relevant },
          { value: 'not_relevant', label: 'Not relevant', count: stats.notRelevant },
          { value: 'unset', label: 'Unset', count: stats.unset },
        ],
      },
    ],
    [relevanceFilters, stats],
  );

  const hasActiveFilters = relevanceFilters.length > 0;

  // Expand-all / collapse-all helpers. Expand-all uses the
  // *filtered* tree so when a relevance filter is active, only the
  // currently-visible sections + categories get expanded.
  const handleExpandAll = useCallback(() => {
    const allIds = [];
    filteredTreeRows.forEach((section) => {
      allIds.push(section.id);
      (section.children || []).forEach((cat) => allIds.push(cat.id));
    });
    setExpandedIds(allIds);
  }, [filteredTreeRows]);

  const handleCollapseAll = useCallback(() => {
    setExpandedIds([]);
  }, []);

  const getRowChildren = useCallback((row) => row.children ?? null, []);

  // Notion-style per-depth backgrounds — uses DataTable's
  // `rowClassName` hook (added in Step 1 of this refactor). Classes
  // are token-driven so they auto-flip light/dark.
  const rowClassName = useCallback(
    (_row, depth) =>
      depth === 0 ? styles.sectionRow : depth === 1 ? styles.categoryRow : styles.itemRow,
    [],
  );

  const columns = useMemo(
    () => [
      {
        key: 'label',
        label: 'Item',
        render: (row) => {
          if (row._kind === 'section') {
            return (
              <span>
                <span className={styles.sectionLabel}>{row.label}</span>
                <span className={styles.dim}>
                  {' · '}
                  {row.categoryCount} categories · {row.itemCount} items
                </span>
              </span>
            );
          }
          if (row._kind === 'category') {
            return (
              <span>
                <span className={styles.categoryNumber}>{row.category_number}</span>
                <span className={styles.categoryTitle}>{row.category_title}</span>
              </span>
            );
          }
          const isEditing = editingItemId === row.item_id;
          return (
            <div className={styles.itemBody}>
              <div className={styles.itemHead}>
                <CategoryPill label={row.item_id} size="sm" />
                <span className={styles.itemDescription}>{row.description}</span>
              </div>
              {isEditing ? (
                <RationaleEditor
                  current={row.rationale}
                  onSave={(rationale) => {
                    handleUpdateItem(row.item_id, { rationale });
                    setEditingItemId(null);
                  }}
                  onCancel={() => setEditingItemId(null)}
                />
              ) : (
                <RationaleCell
                  rationale={row.rationale}
                  onEdit={() => setEditingItemId(row.item_id)}
                />
              )}
            </div>
          );
        },
      },
      {
        key: 'relevance',
        label: 'Relevance',
        width: 200,
        render: (row) =>
          row._kind === 'item' ? (
            <RelevanceCell
              value={row.relevance}
              disabled={savingItemId === row.item_id}
              onChange={(relevance) => handleUpdateItem(row.item_id, { relevance })}
            />
          ) : null,
      },
      {
        // Actions column — kebab on item rows only.
        key: '_actions',
        label: '',
        width: 56,
        align: 'right',
        render: (row) =>
          row._kind === 'item' ? (
            <RowActionsMenu
              ariaLabel={`Actions for ${row.item_id}`}
              actions={[
                {
                  id: 'edit-rationale',
                  label: 'Edit rationale',
                  onClick: () => setEditingItemId(row.item_id),
                },
              ]}
            />
          ) : null,
      },
    ],
    [editingItemId, savingItemId, handleUpdateItem],
  );

  if (error) {
    return <div className={styles.statusError}>Error: {error}</div>;
  }

  // Stats strip uses the canonical TableToolbar:
  //   - count chip → filtered total
  //   - leadingActions (left-grouped, flush right of the count) →
  //     the three per-relevance CategoryPills
  //   - actions (right-grouped) → expand-all / collapse-all
  // The filter button activates the FilterDrawer to the left.
  const toolbar = (
    <TableToolbar
      onFilterToggle={() => setFilterOpen((o) => !o)}
      filterButtonActive={filterOpen || hasActiveFilters}
      count={{ value: filteredItemCount, label: 'items' }}
      leadingActions={
        <>
          <CategoryPill label={`${stats.relevant} Relevant`} tone="success" size="sm" />
          <CategoryPill label={`${stats.notRelevant} Not relevant`} tone="error" size="sm" />
          <CategoryPill label={`${stats.unset} Unset`} tone="neutral" size="sm" />
        </>
      }
      actions={
        <>
          <button type="button" className={styles.toolbarButton} onClick={handleExpandAll}>
            Expand all
          </button>
          <button type="button" className={styles.toolbarButton} onClick={handleCollapseAll}>
            Collapse all
          </button>
        </>
      }
    />
  );

  return (
    <div className={`${dtStyles.shell} ${dtStyles.shellPlain}`}>
      <FilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
      />
      <DataTable
        data={filteredTreeRows}
        columns={columns}
        rowKey="id"
        getRowChildren={getRowChildren}
        expandedIds={expandedIds}
        onExpandedChange={setExpandedIds}
        rowClassName={rowClassName}
        loading={loading}
        toolbar={toolbar}
        emptyState={{
          title: hasActiveFilters ? 'No matching items' : 'No checklist data yet',
          description: hasActiveFilters
            ? 'No items match the selected relevance filters.'
            : 'The UN R155 checklist will appear here once the analysis is loaded.',
        }}
      />
    </div>
  );
};

export default UNR155Tree;
